import type {
  ListMarketAuditLogsReply,
  ListMarketCategoriesReply,
  ListMarketItemsReply,
  ListMarketReleasesReply,
  MarketApplicationTarget,
  MarketCategory,
  MarketItem,
  MarketItemType,
  MarketRelease,
  MarketType,
  ProtoInt64,
} from '@/types';
import api from './api';

function basePath(target: MarketApplicationTarget) {
  return `/api/v1/admin/market/${encodeURIComponent(target)}`;
}

function itemPath(
  target: MarketApplicationTarget,
  itemType: MarketItemType,
  itemKey: string,
) {
  return `${basePath(target)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemKey)}`;
}

export const marketApi = {
  listItems: async (
    target: MarketApplicationTarget,
    itemType?: MarketItemType,
  ): Promise<ListMarketItemsReply> =>
    api.post(`${basePath(target)}/items/list`, { itemType: itemType ?? '' }),

  listCategories: async (
    target: MarketApplicationTarget,
    params: {
      marketType: MarketType;
      itemType: MarketItemType;
      includeDisabled?: boolean;
    },
  ): Promise<ListMarketCategoriesReply> =>
    api.post(`${basePath(target)}/categories/list`, params),

  upsertCategory: async (
    target: MarketApplicationTarget,
    params: {
      itemType: MarketItemType;
      categoryId: string;
      displayNameZh: string;
      displayNameEn: string;
      sortOrder: number;
      enabled: boolean;
      expectedLockVersion: ProtoInt64;
    },
  ): Promise<{ category: MarketCategory }> =>
    api.post(
      `${basePath(target)}/categories/${encodeURIComponent(params.itemType)}/${encodeURIComponent(params.categoryId)}:upsert`,
      params,
    ),

  listReleases: async (
    target: MarketApplicationTarget,
    itemType: MarketItemType,
    itemKey: string,
  ): Promise<ListMarketReleasesReply> =>
    api.post(`${itemPath(target, itemType, itemKey)}/releases/list`, {
      itemType,
      itemKey,
    }),

  setActiveRelease: async (
    target: MarketApplicationTarget,
    params: {
      itemType: MarketItemType;
      itemKey: string;
      version: string;
      expectedLockVersion: ProtoInt64;
    },
  ): Promise<{ item: MarketItem }> =>
    api.post(
      `${itemPath(target, params.itemType, params.itemKey)}/active-release:set`,
      params,
    ),

  setListing: async (
    target: MarketApplicationTarget,
    params: {
      itemType: MarketItemType;
      itemKey: string;
      marketType: MarketType;
      isListed: boolean;
      expectedIsListed: boolean;
      categoryId: string;
      categoryRank: number;
      featured: boolean;
      featuredRank: number;
    },
  ): Promise<{ item: MarketItem }> =>
    api.post(
      `${itemPath(target, params.itemType, params.itemKey)}/listing:set`,
      params,
    ),

  revokeRelease: async (
    target: MarketApplicationTarget,
    params: {
      itemType: MarketItemType;
      itemKey: string;
      version: string;
    },
  ): Promise<{ release: MarketRelease }> =>
    api.post(
      `${itemPath(target, params.itemType, params.itemKey)}/releases/${encodeURIComponent(params.version)}:revoke`,
      params,
    ),

  deleteItem: async (
    target: MarketApplicationTarget,
    params: {
      itemType: MarketItemType;
      itemKey: string;
      expectedLockVersion: ProtoInt64;
    },
  ): Promise<{ itemType: MarketItemType; itemKey: string }> =>
    api.post(
      `${itemPath(target, params.itemType, params.itemKey)}:delete`,
      params,
    ),

  listAuditLogs: async (
    target: MarketApplicationTarget,
    itemType: MarketItemType,
    itemKey: string,
    limit = 100,
  ): Promise<ListMarketAuditLogsReply> =>
    api.post(`${itemPath(target, itemType, itemKey)}/audit-logs/list`, {
      itemType,
      itemKey,
      limit,
    }),
};
